import React from "react"
import { ChevronDown } from "lucide-react"

import { Button } from "@/components/ui/button"
import { EditIcon, type EditIconHandle } from "@/components/ui/edit"
import {
  LayoutPanelTopIcon,
  type LayoutPanelTopIconHandle,
} from "@/components/ui/layout-panel-top-icon"
import { PlusIcon, type PlusIconHandle } from "@/components/ui/plus-icon"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu-v2"
import { useDashboardLayoutStore } from "../layout/dashboardLayoutStore"
import { cn } from "@/lib/utils"

interface DashboardLayoutsMenuProps {
  onEditLayout?: (layoutId: string) => void
  onCreateNewLayout?: () => void
  /** Icon square only (e.g. dashboard mobile header). */
  iconOnly?: boolean
}

const DashboardLayoutsMenu: React.FC<DashboardLayoutsMenuProps> = ({
  onEditLayout,
  onCreateNewLayout,
  iconOnly = false,
}) => {
  const { layouts, activeLayoutId, setActiveLayout } = useDashboardLayoutStore()

  const activeLayout = React.useMemo(
    () => layouts.find((layout) => layout.id === activeLayoutId),
    [activeLayoutId, layouts]
  )

  const handleEditLayout = React.useCallback(() => {
    if (!activeLayoutId) return
    setActiveLayout(activeLayoutId, { preserveEditorReset: true })
    onEditLayout?.(activeLayoutId)
  }, [activeLayoutId, onEditLayout, setActiveLayout])

  const iconRef = React.useRef<LayoutPanelTopIconHandle | null>(null)
  const editIconRef = React.useRef<EditIconHandle | null>(null)
  const plusIconRef = React.useRef<PlusIconHandle | null>(null)

  const startIconAnimation = React.useCallback(() => {
    void iconRef.current?.startAnimation()
  }, [])

  const stopIconAnimation = React.useCallback(() => {
    void iconRef.current?.stopAnimation()
  }, [])

  const startEditIconAnimation = React.useCallback(() => {
    void editIconRef.current?.startAnimation()
  }, [])

  const stopEditIconAnimation = React.useCallback(() => {
    void editIconRef.current?.stopAnimation()
  }, [])

  const startPlusIconAnimation = React.useCallback(() => {
    void plusIconRef.current?.startAnimation()
  }, [])

  const stopPlusIconAnimation = React.useCallback(() => {
    void plusIconRef.current?.stopAnimation()
  }, [])

  const iconHoverHandlers = React.useMemo(
    () => ({
      onPointerDownCapture: startIconAnimation,
      onMouseEnter: startIconAnimation,
      onMouseLeave: stopIconAnimation,
    }),
    [startIconAnimation, stopIconAnimation]
  )

  const editIconHoverHandlers = React.useMemo(
    () => ({
      onPointerDownCapture: startEditIconAnimation,
      onMouseEnter: startEditIconAnimation,
      onMouseLeave: stopEditIconAnimation,
    }),
    [startEditIconAnimation, stopEditIconAnimation]
  )

  const plusIconHoverHandlers = React.useMemo(
    () => ({
      onPointerDownCapture: startPlusIconAnimation,
      onMouseEnter: startPlusIconAnimation,
      onMouseLeave: stopPlusIconAnimation,
    }),
    [startPlusIconAnimation, stopPlusIconAnimation]
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size={iconOnly ? "icon" : "sm"}
          data-font="primary"
          data-dashboard-layouts-trigger=""
          className={cn(
            iconOnly
              ? "h-8 w-8 shrink-0"
              : "h-8 shrink-0 justify-between gap-1.5 font-normal normal-case tracking-normal"
          )}
          aria-label="Dashboard layouts"
          {...iconHoverHandlers}
        >
          {iconOnly ? (
            <LayoutPanelTopIcon
              ref={iconRef}
              size={16}
              className="shrink-0 text-current"
              aria-hidden
            />
          ) : (
            <>
              <span className="flex min-w-0 items-center gap-1.5">
                <LayoutPanelTopIcon
                  ref={iconRef}
                  data-icon="inline-start"
                  size={16}
                  className="shrink-0 text-current"
                  aria-hidden
                />
                <span className="truncate">Layouts</span>
              </span>
              <ChevronDown
                data-icon="inline-end"
                className="size-4 shrink-0 opacity-80"
                aria-hidden
              />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        data-dashboard-layouts-menu=""
        className="z-[2000]"
      >
        <DropdownMenuGroup>
          <DropdownMenuRadioGroup
            value={activeLayoutId}
            onValueChange={(layoutId) => setActiveLayout(layoutId)}
          >
            {layouts.map((layout) => (
              <DropdownMenuRadioItem key={layout.id} value={layout.id}>
                <span className="truncate whitespace-nowrap">{layout.name}</span>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
        {activeLayout && !activeLayout.isDefault && onEditLayout ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onSelect={handleEditLayout} {...editIconHoverHandlers}>
                <EditIcon
                  ref={editIconRef}
                  size={16}
                  className="shrink-0 text-current"
                  aria-hidden
                />
                <span className="whitespace-nowrap">Edit layout</span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => onCreateNewLayout?.()} {...plusIconHoverHandlers}>
          <PlusIcon
            ref={plusIconRef}
            size={16}
            className="shrink-0 text-current"
            aria-hidden
          />
          <span className="whitespace-nowrap">Add new layout</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default DashboardLayoutsMenu
